/** @jsx jsx */
/** @jsxFrag null */
import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import type {VNode} from 'snabbdom';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash/debounce';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {SidenavEl, SidenavJsxProps} from 'common/components/sidenav';
import {SidenavHeader, SidenavSection} from 'common/components/sidenav/types';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';
import {userDetailsStore} from 'common/report/stores/user-details';
import {SIDENAV_COLLAPSED_WIDTH} from 'common/components/sidenav/constants';
import {layoutManager} from 'common/report/util/layout-manager';
import {isTestEnv} from 'common/report/util/env';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getDownloadMode, isBoardsToReportsEnabled, isPublicMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {DummyUrlManager} from 'common/contexts/url-manager/dummy';
import {panel2react} from 'common/util/react';
import type {ReportBanners} from 'common/widgets/report-banners';
import {TemplateType} from 'common/report/dashboards/types';
import {RHSPanelEl} from 'common/widgets/rhs-panel';
import {RHSPanelMode} from 'common/widgets/rhs-panel/types';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ContentFrame} from 'common/widgets/sidenav-layout/content-frame';

import {BROWSE_MODE_KEY} from '../browse/types';
import {MIN_PAGE_WIDTH} from './content-frame';
import css from './index.inline.styl';

const RHS_PANEL_WIDTH = 320;

type SidenavLayoutState = {
  sidenavWidth: number;
  maxSidenavWidth: number;
};

type SidenavLayoutJsxProps = {
  attrs?: {
    'hide-nav'?: boolean;
    'selected-nav-item'?: string;
    'no-padding'?: boolean;
    'is-sidenav-size-fixed'?: boolean;
    loaded?: boolean;
    'storage-key'?: string;
    'is-nested-dashboards-enabled'?: boolean;
    'exclude-banners'?: boolean;
  };
  props?: {
    revealedBannerIdentifiers?: Set<string>;
    navHeader?: null | SidenavHeader;
    navSections?: SidenavSection[];
    preventDefaultNavigate?: () => boolean;
    dashboardTemplateType?: TemplateType;
    implementedData?: boolean;
    showTemplateRHS?: boolean;
    isLoading?: boolean;
    isSeeAllTemplatesLoading?: boolean;
    showRHSReminderTooltip?: boolean;
  };
  on?: Partial<
    Pick<SidenavJsxProps['on'], 'selectItem' | 'headerAction' | 'footerAction' | 'dragDrop' | 'overflowMenuSelect'> & {
      contentFrameResized: (ev: CustomEvent<{padding: number}>) => void;
      copyDemoToProject: (ev: CustomEvent<{targetProjectId?: number}>) => void;
      updateBlueprint: (ev: CustomEvent<any>) => void;
      commitBlueprint: (ev: CustomEvent<any>) => void;
      clickSeeAllTemplates: (ev: CustomEvent<any>) => void;
      clickRHS: (ev: CustomEvent<any>) => void;
    }
  >;
};

export class SidenavLayout extends Component<SidenavLayoutState, EmptyObj, EmptyObj, EmptyObj, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<SidenavLayoutJsxProps['attrs']> {
    return {
      'hide-nav': {
        type: `boolean`,
        description: `This hides the nav area entirely (different from collapsing), useful for views where some modes do not need a nav`,
      },
      'selected-nav-item': {type: `string`, description: `value of selected item`},
      loaded: {type: `boolean`},
      'is-sidenav-size-fixed': {type: `boolean`},
      'is-nested-dashboards-enabled': {type: `boolean`},
      'no-padding': {
        type: `boolean`,
        description: `This value ensures that there is no additional margins or padding on the elements inside SidenavLayout`,
      },
      'storage-key': {type: `string`, default: `default`},
      'exclude-banners': `boolean`,
    };
  }

  private selfResizeObserver: ResizeObserver | null = null;
  private selfWidth = 0;
  private userId = -1;
  private hasInitializedRequiredNavData = false;

  private paddingFrameResizeObserver: ResizeObserver | null = null;

  public get config(): ConfigOptions<SidenavLayoutState, EmptyObj, IronContextRegistry> {
    return {
      contexts: [`urlManager` as const, `currentProject` as const],
      defaultContexts: {
        urlManager: new DummyUrlManager(),
      },
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {
        sidenavWidth: 0,
        maxSidenavWidth: 0,
      },
      helpers: {
        isOnDiscoverPage: () => {
          return this.attr(`selected-nav-item`) === BROWSE_MODE_KEY;
        },
      },
    };
  }

  private _revealedBannerIds: Set<string> = new Set();
  public set revealedBannerIdentifiers(value: Set<string>) {
    this._revealedBannerIds = value;
    const reportBannersEl = this.shadowRoot.querySelector<ReportBanners>(`mp-report-banners`);
    if (reportBannersEl) {
      reportBannersEl.revealedBannerIdentifiers = value;
      this.update();
    }
  }
  public get revealedBannerIdentifiers(): Set<string> {
    return this._revealedBannerIds;
  }

  private _dashboardTemplateType: TemplateType | undefined = undefined;
  public set dashboardTemplateType(value: TemplateType | undefined) {
    this._dashboardTemplateType = value;
    this.update();
  }

  public get dashboardTemplateType(): TemplateType | undefined {
    return this._dashboardTemplateType;
  }

  private _implementedData = false;
  public set implementedData(value: boolean) {
    this._implementedData = value;
    this.update();
  }

  public get implementedData(): boolean {
    return this._implementedData;
  }

  private _navHeader: SidenavHeader | null = null;
  public set navHeader(value: SidenavHeader | null) {
    this._navHeader = value;
    this.initRequiredNavData();
    this.update();
  }
  public get navHeader(): SidenavHeader | null {
    return this._navHeader;
  }

  private _navSections: SidenavSection[] = [];
  public set navSections(value: SidenavSection[]) {
    this._navSections = value;
    this.initRequiredNavData();
    this.update();
  }
  public get navSections(): SidenavSection[] {
    return this._navSections;
  }

  private _preventDefaultNavigate?: () => boolean;
  public set preventDefaultNavigate(value: () => boolean) {
    this._preventDefaultNavigate = value;
    this.update();
  }
  public get preventDefaultNavigate(): () => boolean {
    return this._preventDefaultNavigate;
  }

  private _showTemplateRHS = false;
  public set showTemplateRHS(value: boolean) {
    this._showTemplateRHS = value;
    this.update();
  }
  public get showTemplateRHS(): boolean {
    return this._showTemplateRHS;
  }

  private _isLoading = false;
  public set isLoading(value: boolean) {
    this._isLoading = value;
    this.update();
  }
  public get isLoading(): boolean {
    return this._isLoading;
  }

  private _isSeeAllTemplatesLoading = false;
  public set isSeeAllTemplatesLoading(value: boolean) {
    this._isSeeAllTemplatesLoading = value;
    this.update();
  }
  public get isSeeAllTemplatesLoading(): boolean {
    return this._isSeeAllTemplatesLoading;
  }

  private _showRHSReminderTooltip = false;
  public set showRHSReminderTooltip(value: boolean) {
    this._showRHSReminderTooltip = value;
    this.update();
  }
  public get showRHSReminderTooltip(): boolean {
    return this._showRHSReminderTooltip;
  }

  public connectedCallback() {
    const debouncedUpdateLayout = debounce(this.updateLayout.bind(this), 100, {trailing: true});
    this.selfResizeObserver = new ResizeObserver((entries) => {
      this.selfWidth = entries[0].contentRect.width;
      debouncedUpdateLayout();
    });

    super.connectedCallback();

    this.selfResizeObserver.observe(this);
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    this.paddingFrameResizeObserver?.disconnect();
    if (!isTestEnv()) {
      layoutManager.setConfig({bannerContainer: null});
    }
    super.disconnectedCallback();
  }

  public getCurrentProject(): CurrentProject | undefined {
    return isPublicMode() ? undefined : this.getContext(`currentProject`).get().value;
  }

  private renderSidenav(): VNode {
    return (
      <SidenavEl
        sel='.sidenav'
        class={{'hide-nav': this.attr(`hide-nav`)}}
        style={{
          width: this.state.sidenavWidth > 0 ? `${this.state.sidenavWidth}px` : undefined,
          'max-width':
            this.state.maxSidenavWidth > SIDENAV_COLLAPSED_WIDTH ? `${this.state.maxSidenavWidth}px` : undefined,
          'min-width': `${SIDENAV_COLLAPSED_WIDTH}px`,
        }}
        attrs={{
          elref: `sidenav`,
          selected: this.attr(`selected-nav-item`),
          loading: !this.attr(`loaded`),
          header: this.navHeader,
          'min-adjacent-width': this.getMinContentWidth(),
          'user-id': this.userId,
          'storage-key': this.attr(`storage-key`),
          'fixed-size': this.attr(`is-sidenav-size-fixed`),
          'is-nested-dashboards-enabled': this.attr(`is-nested-dashboards-enabled`),
          'is-trial-org': this.getCurrentProject()?.isTrialOrg,
          'trial-end-date': this.getCurrentProject()?.trialEndDate,
          'utc-offset': this.getCurrentProject()?.utcOffset,
        }}
        on={{
          selectItem: this.passThroughEvent,
          headerAction: this.passThroughEvent,
          footerAction: this.passThroughEvent,
          dragDrop: this.passThroughEvent,
          overflowMenuSelect: this.passThroughEvent,
          sidenavResize: (ev: CustomEvent<{width: number}>) => {
            this.update({sidenavWidth: ev.detail.width});
            this.updateLayout();
          },
        }}
        props={{
          sections: this.navSections,
          preventDefaultNavigate: this.preventDefaultNavigate,
        }}
        hook={stickyElemHooks}
      >
        {/* Note: the (`slot` in this.navHeader) prop check is for Typescript type discrimination, during runtime it's redundant */}
        {this.navHeader && `slot` in this.navHeader && this.navHeader.slot && (
          <slot attrs={{name: this.navHeader.slot, slot: this.navHeader.slot}} />
        )}
      </SidenavEl>
    );
  }

  private renderBannerContainer(): VNode {
    return (
      <div
        sel='.banner-container'
        class={{'no-padding': this.attr(`no-padding`)}}
        hook={{
          insert: (vnode: VNode) => {
            // don't use layoutManager in test env because some test suites aren't setup to mock banner API requests
            if (!isTestEnv()) {
              layoutManager.setConfig({
                bannerContainer: vnode.elm as HTMLElement,
              });
            }
          },
        }}
      />
    );
  }

  private renderTemplate(): VNode {
    const isDownloadMode = getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;
    const isBoardsToReportsEnabledVal = isBoardsToReportsEnabled(this.getCurrentProject());

    return (
      <div
        sel='.root'
        class={{
          'boards-to-reports': isBoardsToReportsEnabledVal,
          'inherit-size': isBoardsToReportsEnabledVal,
        }}
      >
        {this.hasRequiredData() && (
          <>
            {this.hasSidenavConfiguration() && this.renderSidenav()}
            <ContentFrame
              sel='.content-area'
              minContentWidth={this.getMinContentWidth()}
              includePadding={!this.attr(`no-padding`)}
              isDownloadMode={isDownloadMode}
              isBoardsToReportsEnabled={isBoardsToReportsEnabledVal}
              isReportScrollContainer={isBoardsToReportsEnabledVal}
              onPaddingFrameInsert={(vnode) => {
                const paddingFrameEl = vnode.elm as HTMLElement;
                this.paddingFrameResizeObserver = new ResizeObserver(() => {
                  const padding = parseFloat(
                    window.getComputedStyle(paddingFrameEl, null).getPropertyValue(`padding-left`),
                  );
                  this.dispatchEvent(
                    new CustomEvent(`contentFrameResized`, {
                      detail: {
                        padding,
                      },
                    }),
                  );
                });
                this.paddingFrameResizeObserver.observe(paddingFrameEl);
              }}
            >
              {!this.attr(`exclude-banners`) && this.renderBannerContainer()}
              <slot sel='.content-slot' />
            </ContentFrame>
            {!isBoardsToReportsEnabledVal &&
              ((this.showStarterBoardRHSPanel() && <RHSPanelEl params={{mode: RHSPanelMode.StarterBoard}} />) ||
                (this.showTemplateRHSPanel() && (
                  <RHSPanelEl
                    sel='.rhs-templates-shadow'
                    on={{
                      updateBlueprint: (ev) => this.passThroughEvent(ev),
                      commitBlueprint: (ev) => this.passThroughEvent(ev),
                      clickSeeAllTemplates: (ev) => this.passThroughEvent(ev),
                      click: () => this.dispatchEvent(new CustomEvent(`clickRHS`)),
                    }}
                    params={{
                      mode: RHSPanelMode.Templates,
                      selectedTemplate: this.dashboardTemplateType,
                      isLoading: this.isLoading,
                      isSeeAllTemplatesLoading: this.isSeeAllTemplatesLoading,
                      showReminderTooltip: this.showRHSReminderTooltip,
                    }}
                  />
                )))}
          </>
        )}
      </div>
    );
  }

  private showStarterBoardRHSPanel(): boolean {
    return (
      !this.config.helpers.isOnDiscoverPage() &&
      this.dashboardTemplateType === TemplateType.STARTER_BOARD &&
      !this.implementedData
    );
  }
  private showTemplateRHSPanel(): boolean {
    return !this.config.helpers.isOnDiscoverPage() && this.showTemplateRHS;
  }

  private getMinContentWidth(): number {
    const isSidenavVisible = this.hasSidenavConfiguration() && !this.attr(`hide-nav`);
    const isRHSOpen = this.showTemplateRHSPanel() || this.showStarterBoardRHSPanel();
    return isSidenavVisible
      ? MIN_PAGE_WIDTH - (this.state.sidenavWidth + (isRHSOpen ? RHS_PANEL_WIDTH : 0))
      : MIN_PAGE_WIDTH;
  }

  private passThroughEvent = (ev: CustomEvent): void => {
    ev.stopPropagation();
    this.dispatchEvent(new CustomEvent(ev.type, ev));
  };

  // hasRequiredData() will block all rendering until it is true
  private hasRequiredData(): boolean {
    // if we plan to show a nav, block all rendering on required nav data to avoid layout jiggling
    // where content is noticeably resized as the nav asynchronously loads
    if (this.hasSidenavConfiguration()) {
      return this.userId !== -1;
    }

    return true;
  }

  // hasSidenavConfiguration() will block nav components from being rendered until it is true
  private hasSidenavConfiguration(): boolean {
    return !!this.navHeader || this.navSections.length > 0;
  }

  private initRequiredNavData(): void {
    if (!this.hasRequiredData() && !this.hasInitializedRequiredNavData) {
      const cookieUser = userCookieUtils.getUserIdentityFromCookie();
      if (cookieUser) {
        this.userId = cookieUser.id;
        this.update();
      } else {
        // fallback in environments without accessible identity cookie (eg. localhost development, tests)
        useStoreAndFetchOnce(this, userDetailsStore, () => {
          this.userId = userDetailsStore.value.id;
          this.update();
        });
      }
      this.hasInitializedRequiredNavData = true;
    }
  }

  private updateLayout(): void {
    // check isConnected to avoid theoretical scenario in which updateLayout() is debounced and called after disconnection
    if (this.isConnected && this.selfWidth > 0) {
      this.update({
        maxSidenavWidth: this.selfWidth - this.getMinContentWidth(),
      });
    }
  }
}

export const SidenavLayoutEl = defineAndMakeJsxFactory<SidenavLayoutJsxProps>(`mp-sidenav-layout`, SidenavLayout);
export const SidenavLayoutReact = panel2react<SidenavLayoutJsxProps>(`mp-sidenav-layout`, SidenavLayout);
